/** Shield scope vocabulary, shared by the explorer banner and the Shield console. */

export function isChainScopeType(scopeType: string): boolean {
	return (
		scopeType === "chain" ||
		scopeType === "chain_all" ||
		scopeType === "intents"
	);
}

export function isTokenScopeType(scopeType: string): boolean {
	return scopeType === "token" || scopeType === "token_chain";
}

/** `chain` is the external-chain leg, `intents` the Intents-held leg, `chain_all` both. */
const SCOPE_TYPE_LABELS: Record<string, string> = {
	chain_all: "chain and intents",
	token_chain: "token, on-chain deposits",
};

export function getScopeTypeLabel(scopeType: string): string {
	return SCOPE_TYPE_LABELS[scopeType] ?? scopeType.replace(/_/g, " ");
}

type Scoped = {
	scopeType: string;
	scopeValue: string;
};

export type ScopeGroup<T extends Scoped> = {
	scopeType: string;
	scopeValue: string;
	incidents: [T, ...T[]];
};

export function scopeKey({ scopeType, scopeValue }: Scoped): string {
	return `${scopeType}:${scopeValue}`;
}

/** One entry per scope, in the order scopes and members first appear. */
export function groupByScope<T extends Scoped>(
	incidents: readonly T[],
): ScopeGroup<T>[] {
	const groups: ScopeGroup<T>[] = [];

	for (const incident of incidents) {
		const group = groups.find((candidate) => {
			return (
				candidate.scopeType === incident.scopeType &&
				candidate.scopeValue === incident.scopeValue
			);
		});

		if (group === undefined) {
			groups.push({
				scopeType: incident.scopeType,
				scopeValue: incident.scopeValue,
				incidents: [incident],
			});
		} else {
			group.incidents.push(incident);
		}
	}

	return groups;
}

/** Oldest open, by instant: the strings carry no precision guarantee. */
export function earliest<T extends { createdAt: string }>(
	incidents: [T, ...T[]],
): T {
	return incidents.reduce((oldest, incident) => {
		return Date.parse(incident.createdAt) < Date.parse(oldest.createdAt)
			? incident
			: oldest;
	});
}
